import { useState } from 'react'
import { Copy, Check } from 'lucide-react'

interface BadgeEmbedProps {
  scanId: string
}

export default function BadgeEmbed({ scanId }: BadgeEmbedProps) {
  const [copiedFormat, setCopiedFormat] = useState<string | null>(null)

  const badgeUrl = `${window.location.origin}/api/v1/scan/${scanId}/badge`
  const scanUrl = `${window.location.origin}/scan/${scanId}`

  const embedCode = `<a href="${scanUrl}">
  <img src="${badgeUrl}" alt="WCAG Checked" />
</a>`

  const copyToClipboard = async (code: string, format: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedFormat(format)
      setTimeout(() => setCopiedFormat(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Share Your Results</h3>

      {/* Badge Preview */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Badge Preview</label>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 inline-flex">
          <a href={scanUrl} target="_blank" rel="noopener noreferrer">
            <img src={badgeUrl} alt="WCAG Checked" />
          </a>
        </div>
      </div>

      {/* Embed Code */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Embed Code</label>
          <button
            onClick={() => copyToClipboard(embedCode, 'html')}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {copiedFormat === 'html' ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-green-600">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy
              </>
            )}
          </button>
        </div>

        <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
          <pre className="text-sm text-gray-100 font-mono">
            <code>{embedCode}</code>
          </pre>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">How to use:</h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Copy the HTML embed code</li>
          <li>Paste it into your website</li>
          <li>Badge will display your WCAG compliance score</li>
          <li>Clicking the badge links back to achecker.ca</li>
        </ul>
      </div>
    </div>
  )
}
